import { Box, useTheme, Button } from "@mui/material";
import { toPng } from "html-to-image";
import { forwardRef, useImperativeHandle, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import DownloadComponentButton from "../button/DownloadComponentButton";
import SecondaryTabs from "../tabs/SecondaryTabs";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";

export const getBarOptions = (theme) => {
  const isDark = theme?.palette?.mode === "dark";
  const graphText = isDark ? "#ffffff" : "#0f172a";
  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: {
      x: {
        stacked: true,
        type: "logarithmic",
        grid: {
          display: false,
          drawBorder: false,
        },
        border: { display: true, width: 2, color: graphText },
        title: {
          display: true,
          text: "GWP [kg CO₂e]",
          font: { family: GRAPH_PALETTE.font.family, size: 14, weight: GRAPH_PALETTE.font.weights.axisLabel },
          padding: { top: 14 },
          color: graphText,
        },
        ticks: {
          maxTicksLimit: 10,
          font: { family: GRAPH_PALETTE.font.family, weight: GRAPH_PALETTE.font.weights.standard, size: 14 },
          color: graphText,
        },
      },
      y: {
        stacked: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        border: { display: true, width: 2, color: graphText },
        ticks: {
          autoSkip: false,
          callback: function(value) {
            const label = this.getLabelForValue(value);
            return label && label.length > 30 ? label.substring(0, 30) + "…" : label;
          },
          font: { family: GRAPH_PALETTE.font.family, weight: GRAPH_PALETTE.font.weights.standard, size: 14 },
          color: graphText,
        },
      },
    },
    layout: { padding: { right: 50, left: 50, top: 10, bottom: 10 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.8)",
        titleColor: isDark ? "#000" : "#fff",
        bodyColor: isDark ? "#000" : "#fff",
        titleFont: { family: GRAPH_PALETTE.font.family, weight: GRAPH_PALETTE.font.weights.medium, size: 14 },
        bodyFont: { family: GRAPH_PALETTE.font.family, weight: GRAPH_PALETTE.font.weights.standard, size: 14 },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          title: (items) => items[0]?.label || "",
          label: (context) => {
            let value = context.raw;
            return `${context.dataset.label || "Value"}: ${Number(value).toFixed(2)}`;
          },
        },
    },
    datalabels: { 
      display: true,
      align: 'right',
      anchor: 'end',
      color: isDark ? "#e2e8f0" : "#333333",
      font: { weight: GRAPH_PALETTE.font.weights.medium, size: 14, family: GRAPH_PALETTE.font.family },
      formatter: (value, context) => {
        const dataArr = context.dataset.data;
        const total = dataArr.reduce((a,b) => (Number(a)||0) + (Number(b)||0), 0);
        if (total === 0) return "0%";
        const pct = ((Number(value)||0) / total) * 100;
        if (pct === 0) return "";
        return pct.toFixed(1) + "%";
      }
    }
  }
  };
};
export const barOptions = getBarOptions(false);
const BarChartWithTabs = forwardRef(({ filename, selected, setSelected, tabsOptions = [], data, height = "400px", sx = {} }, parentRef) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dynamicBarOptions = getBarOptions(theme);
  const [visibility, setVisibility] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const ref = useRef(null);

  const totalItems = data?.labels?.length || 0;

  const slicedData = useMemo(() => {
    if (!data) return null;
    if (data.labels.length <= 15 || showAll) return data;
    const slicedLabels = data.labels.slice(0, 15);
    const slicedDatasets = data.datasets.map(dataset => ({
      ...dataset,
      data: dataset.data.slice(0, 15),
      backgroundColor: Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor.slice(0, 15) : dataset.backgroundColor,
      borderColor: Array.isArray(dataset.borderColor) ? dataset.borderColor.slice(0, 15) : dataset.borderColor,
      pointBackgroundColor: Array.isArray(dataset.pointBackgroundColor) ? dataset.pointBackgroundColor.slice(0, 15) : dataset.pointBackgroundColor,
    }));
    return {
      ...data,
      labels: slicedLabels,
      datasets: slicedDatasets
    };
  }, [data, showAll]);

  const chartHeight = useMemo(() => {
    const count = slicedData?.labels?.length || 0;
    return `${count * 35 + 100}px`;
  }, [slicedData]);

  useImperativeHandle(parentRef, () => ({
    getScreenshot: async () => {
      if (ref.current) {
        return await toPng(ref.current, { cacheBust: true, backgroundColor: "#ffffff" });
      }
      return null;
    },
  }));
  return (
    <Box
      onMouseEnter={() => {
        setVisibility(true);
      }}
      onMouseLeave={() => {
        setVisibility(false);
      }}
      style={{ position: "relative" }}
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: chartHeight,
        ...sx
      }}
    >
      <Box style={{ position: "absolute", top: "10px", right: "10px", display: "flex", alignItems: "center", gap: "8px", zIndex: 10 }}>
        {totalItems > 15 && (
          <Button
            size="small"
            onClick={() => setShowAll(!showAll)}
            variant="contained"
            sx={{
              textTransform: "none",
              fontSize: "11px",
              fontWeight: 600,
              py: 0.5,
              px: 1.2,
              borderRadius: "6px",
              backgroundColor: "#659d33",
              color: "#ffffff",
              border: "1px solid #659d33",
              "&:hover": {
                backgroundColor: "#55842b",
                borderColor: "#55842b",
                boxShadow: "0 2px 8px rgba(101, 157, 51, 0.3)"
              }
            }}
          >
            {showAll ? t("Show Top 15") : t("Show All")}
          </Button>
        )}
        <DownloadComponentButton ref={ref} filename={filename} visibility={visibility} />
      </Box>
      <Box ref={ref} sx={{
        backgroundColor: isDark ? "#1c1c1e" : "white",
        flexGrow: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column"
      }}>
        <Box sx={{ display: tabsOptions.length > 0 ? "flex" : "none", justifyContent: "center" }}>
          <SecondaryTabs selected={selected} setSelected={setSelected} options={tabsOptions} />
        </Box>
        <Box sx={{
          minHeight: chartHeight,
          flex: 1,
          position: "relative",
          overflow: "hidden",
        }}>{data && <Bar data={slicedData} options={dynamicBarOptions} />}</Box>
      </Box>
    </Box>
  );
});

export default BarChartWithTabs;
